<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { api, ApiError } from "@/api/client";
import type { OperatorTokenView } from "@/api/types";
import { useAuthStore } from "@/stores/auth";
import { getThemePref, setThemePref, type ThemePref } from "@/theme";
import { getPreviewMode, setPreviewMode, type PreviewMode } from "@/prefs";
import SecretReveal from "@/components/SecretReveal.vue";
import { useConfigStore } from "@/stores/config";
import { appVersion, appRevision } from "@/version";

const { t, locale } = useI18n();
const auth = useAuthStore();
const router = useRouter();
const config = useConfigStore();
const refreshing = ref(false);
const refreshError = ref("");

async function refreshApp() {
  refreshing.value = true;
  refreshError.value = "";
  try {
    const registration = await navigator.serviceWorker?.getRegistration();
    await registration?.update();
    window.location.reload();
  } catch {
    refreshing.value = false;
    refreshError.value = t("settings.refresh_failed");
  }
}

const operator = computed(() => auth.operator());
const loggingOut = ref(false);

const LOCALE_KEY = "nyx.prefs.v1.locale";
const LOCALES = ["en", "ru"] as const;

function setLocale(l: (typeof LOCALES)[number]) {
  locale.value = l;
  localStorage.setItem(LOCALE_KEY, l);
}

const THEMES = ["dark", "light", "auto"] as const;
const theme = ref<ThemePref>(getThemePref());
function setTheme(p: ThemePref) {
  theme.value = p;
  setThemePref(p);
}

const PREVIEWS = ["live", "last", "off"] as const;
const preview = ref<PreviewMode>(getPreviewMode());
function setPreview(p: PreviewMode) {
  preview.value = p;
  setPreviewMode(p);
}

// --- API tokens (operator CLI, §3.1) -------------------------------
const tokens = ref<OperatorTokenView[]>([]);
const tokenName = ref("");
const tokenError = ref<string | null>(null);
const creating = ref(false);
// Plaintext shown once right after creation.
const newToken = ref<string | null>(null);

function tokenErr(e: unknown): string {
  if (e instanceof ApiError) return `${e.code} (HTTP ${e.status})`;
  if (e instanceof Error) return e.message;
  return "error";
}

async function loadTokens() {
  try {
    tokens.value = (await api.tokens.list()).tokens.filter((t) => t.status === "enabled");
  } catch (e) {
    tokenError.value = tokenErr(e);
  }
}

async function createToken() {
  const name = tokenName.value.trim();
  if (!name || creating.value) return;
  creating.value = true;
  tokenError.value = null;
  try {
    const created = await api.tokens.create(name);
    newToken.value = created.value;
    tokenName.value = "";
    await loadTokens();
  } catch (e) {
    tokenError.value = tokenErr(e);
  } finally {
    creating.value = false;
  }
}

async function revokeToken(id: string) {
  if (!window.confirm(t("settings.token_revoke_confirm"))) return;
  try {
    await api.tokens.revoke(id);
    await loadTokens();
  } catch (e) {
    tokenError.value = tokenErr(e);
  }
}

onMounted(loadTokens);

async function doLogout() {
  if (loggingOut.value) return;
  loggingOut.value = true;
  try {
    await auth.logout();
    await router.push({ name: "login" });
  } finally {
    loggingOut.value = false;
  }
}
</script>

<template>
  <section class="settings">
    <h1>{{ t("settings.title") }}</h1>

    <section class="settings__card" aria-labelledby="about-app">
      <h2 id="about-app">{{ t("settings.about") }}</h2>
      <dl class="kv">
        <dt>{{ t("settings.app_version") }}</dt>
        <dd data-test="app-version">{{ appVersion }}</dd>
        <dt>{{ t("settings.app_revision") }}</dt>
        <dd data-test="app-revision">
          <code>{{ appRevision }}</code>
        </dd>
        <dt>{{ t("settings.server_version") }}</dt>
        <dd>{{ config.state.kind === "ready" ? config.state.config.version : "—" }}</dd>
      </dl>
      <button class="settings__locale" type="button" :disabled="refreshing" @click="refreshApp">
        {{ refreshing ? t("settings.refreshing") : t("settings.refresh_app") }}
      </button>
      <p v-if="refreshError" class="admin-error" role="alert">{{ refreshError }}</p>
    </section>

    <section class="settings__card">
      <h2>{{ t("settings.account") }}</h2>
      <dl v-if="operator" class="kv">
        <dt>{{ t("settings.email") }}</dt>
        <dd>{{ operator.email }}</dd>
        <dt>{{ t("settings.role") }}</dt>
        <dd>{{ t(`role.${operator.role}`) }}</dd>
        <dt>{{ t("settings.organization") }}</dt>
        <dd>{{ operator.organization_id }}</dd>
      </dl>
      <p class="settings__note">{{ t("settings.password_note") }}</p>
    </section>

    <section class="settings__card">
      <h2>{{ t("settings.tokens") }}</h2>
      <p class="settings__note">{{ t("settings.tokens_note") }}</p>

      <SecretReveal
        v-if="newToken"
        :secret="newToken"
        :label="t('settings.token_shown_once')"
        @dismiss="newToken = null"
      />

      <form class="settings__token-form" @submit.prevent="createToken">
        <input
          v-model="tokenName"
          :disabled="creating"
          :placeholder="t('settings.token_name_placeholder')"
        />
        <button type="submit" class="settings__locale" :disabled="creating || !tokenName.trim()">
          {{ creating ? t("admin.submitting") : t("settings.token_create") }}
        </button>
      </form>
      <p v-if="tokenError" class="admin-error">{{ tokenError }}</p>

      <ul v-if="tokens.length" class="settings__tokens">
        <li v-for="tk in tokens" :key="tk.id" class="settings__token">
          <span class="settings__token-name">{{ tk.name }}</span>
          <span class="settings__token-meta">{{ tk.created_at.slice(0, 10) }}</span>
          <button type="button" class="settings__token-revoke" @click="revokeToken(tk.id)">
            {{ t("settings.token_revoke") }}
          </button>
        </li>
      </ul>
      <p v-else class="settings__note">{{ t("settings.tokens_empty") }}</p>
    </section>

    <section class="settings__card">
      <h2>{{ t("settings.preview") }}</h2>
      <div class="settings__locales">
        <button
          v-for="p in PREVIEWS"
          :key="p"
          type="button"
          class="settings__locale"
          :class="{ 'settings__locale--active': preview === p }"
          @click="setPreview(p)"
        >
          {{ t(`settings.preview_${p}`) }}
        </button>
      </div>
      <p class="settings__note">{{ t("settings.preview_note") }}</p>
    </section>

    <section class="settings__card">
      <h2>{{ t("settings.theme") }}</h2>
      <div class="settings__locales">
        <button
          v-for="p in THEMES"
          :key="p"
          type="button"
          class="settings__locale"
          :class="{ 'settings__locale--active': theme === p }"
          @click="setTheme(p)"
        >
          {{ t(`settings.theme_${p}`) }}
        </button>
      </div>
    </section>

    <section class="settings__card">
      <h2>{{ t("settings.language") }}</h2>
      <div class="settings__locales">
        <button
          v-for="l in LOCALES"
          :key="l"
          type="button"
          class="settings__locale"
          :class="{ 'settings__locale--active': locale === l }"
          @click="setLocale(l)"
        >
          {{ t(`settings.lang_${l}`) }}
        </button>
      </div>
    </section>

    <section class="settings__card">
      <button type="button" class="settings__logout" :disabled="loggingOut" @click="doLogout">
        {{ loggingOut ? t("admin.submitting") : t("user.logout") }}
      </button>
    </section>
  </section>
</template>

<style scoped>
.settings {
  max-width: 40rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.settings h1 {
  margin: 0;
}
.settings__card {
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-bg-elev);
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.settings__card h2 {
  margin: 0;
  font-size: 0.95rem;
  color: var(--color-fg-dim);
}
.settings__note {
  margin: 0;
  font-size: 0.8rem;
  color: var(--color-fg-dim);
}
.settings__locales {
  display: flex;
  gap: 0.5rem;
}
.settings__locale {
  padding: 0.45rem 0.9rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-bg);
  color: var(--color-fg);
  cursor: pointer;
  min-height: 44px;
}
.settings__locale--active {
  border-color: var(--color-accent);
  color: var(--color-accent);
  font-weight: 600;
}
.settings__token-form {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.settings__token-form input {
  flex: 1;
  min-width: 10rem;
  padding: 0.45rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-bg);
  color: var(--color-fg);
}
.settings__tokens {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.settings__token {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.4rem 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 0.85rem;
}
.settings__token-name {
  font-weight: 600;
}
.settings__token-meta {
  color: var(--color-fg-dim);
  font-family: var(--font-mono);
  font-size: 0.78rem;
}
.settings__token-revoke {
  margin-left: auto;
  padding: 0.25rem 0.6rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg);
  color: var(--color-error);
  cursor: pointer;
  font-size: 0.78rem;
}
.settings__logout {
  align-self: flex-start;
  padding: 0.5rem 0.95rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg);
  color: var(--color-fg);
  cursor: pointer;
  min-height: 44px;
}
</style>
